/**
 * OrganizationSettingsPage - Main page for organization settings
 */

import type * as React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BankAccountList } from '../components/bank-accounts';
import { ChequeSettingsList } from '../components/cheque-settings';
import { CompanyInfoForm } from '../components/company-info';
import { SignatureList } from '../components/signatures';

type Tab = 'company-info' | 'bank-accounts' | 'signatures' | 'cheque-settings';

interface TabConfig {
  id: Tab;
  label: string;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: 'company-info',
    label: 'Company Info',
    description: 'Manage your organization details',
  },
  {
    id: 'bank-accounts',
    label: 'Bank Accounts',
    description: 'Configure bank accounts for cheques',
  },
  {
    id: 'signatures',
    label: 'Signatures',
    description: 'Upload signature images',
  },
  {
    id: 'cheque-settings',
    label: 'Cheque Settings',
    description: 'Configure cheque printing',
  },
];

export interface OrganizationSettingsPageProps {
  className?: string;
}

export const OrganizationSettingsPage: React.FC<OrganizationSettingsPageProps> = ({
  className,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('company-info');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'company-info':
        return <CompanyInfoForm />;
      case 'bank-accounts':
        return <BankAccountList />;
      case 'signatures':
        return <SignatureList />;
      case 'cheque-settings':
        return <ChequeSettingsList />;
      default:
        return null;
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Organization Settings</h1>
        <p className="text-muted-foreground">
          Manage your organization&apos;s information, bank accounts, and cheque printing settings
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-4" aria-label="Tabs">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant="ghost"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'relative rounded-none border-b-2 px-4 py-2',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground',
              )}
            >
              {tab.label}
            </Button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="py-4">{renderTabContent()}</div>
    </div>
  );
};

OrganizationSettingsPage.displayName = 'OrganizationSettingsPage';
